import { createTheme, Select, SelectProps, SvgIcon, SvgIconProps, ThemeOptions, ThemeProvider } from "@mui/material";
import { ReactNode, useMemo, useRef } from "react";
import CaretDown from "./images/caret-down.svg?react";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";
import { themeOptions } from "../../theme";

function DropdownIcon({ ...props }: SvgIconProps) {
	return (
		<SvgIcon
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			sx={{
				"&.MuiSvgIcon-root": {
					width: 17,
					height: 17,
					top: "calc(50% - 8.5px)",
					color: "#4D4D4D",
				},
			}}
		>
			<CaretDown />
		</SvgIcon>
	);
}

const selectThemeOptions: ThemeOptions = {
	components: {
		MuiSelect: {
			defaultProps: {
				IconComponent: DropdownIcon,
			},
		},
	},
};

function handleShadowDomArrowKeys(event: React.KeyboardEvent, container: HTMLElement) {
	if (!container) return;

	const validKeys = ["ArrowUp", "ArrowDown"];
	if (!validKeys.includes(event.key)) return;

	const listBox = container.querySelector(".MuiMenu-root ul[role='listbox']");
	if (!listBox) return;

	const menuItems = Array.from(listBox.querySelectorAll("li[role='option']"));
	if (menuItems.length === 0) return;

	const rootNode = event.currentTarget.getRootNode();
	const focused = rootNode instanceof ShadowRoot ? rootNode.activeElement : document.activeElement;

	let currentIndex = menuItems.indexOf(focused as Element);

	if (currentIndex === -1) {
		const selected = listBox.querySelector("li.Mui-selected");
		currentIndex = selected ? menuItems.indexOf(selected) : 0;
	}

	let nextIndex = currentIndex;
	if (event.key === "ArrowDown") {
		nextIndex = Math.min(currentIndex + 1, menuItems.length - 1);
	} else if (event.key === "ArrowUp") {
		nextIndex = Math.max(currentIndex - 1, 0);
	}

	if (nextIndex !== currentIndex) {
		event.preventDefault();
		(menuItems[nextIndex] as HTMLElement).focus();
	}
}

export type CoreSelectProps<Value> = Omit<SelectProps<Value>, "inputRef"> & {
	label?: string;
	renderValue?: (value: Value) => ReactNode;
	shadowDomContainer?: HTMLElement | null /**Keyboard breaks in shadowdom */;
};

export default function CoreSelect<Value>({
	label,
	renderValue,
	shadowDomContainer,
	...selectProps
}: CoreSelectProps<Value>) {
	const selectRef = useRef<HTMLElement>(null);
	const theme = useMemo(() => createTheme(themeOptions, selectThemeOptions), []);

	return (
		<ThemeProvider theme={theme}>
			<Select
				{...selectProps}
				inputRef={selectRef}
				{...(renderValue && {
					renderValue: (value: unknown) => (value !== null ? renderValue(value as Value) : label),
				})}
				MenuProps={{
					...selectProps.MenuProps,
					container: shadowDomContainer,
					...(shadowDomContainer && {
						onKeyDown: (e) => handleShadowDomArrowKeys(e, shadowDomContainer),
					}),
				}}
				inputProps={{
					"aria-label": label,
					...selectProps.inputProps,
				}}
			/>
		</ThemeProvider>
	);
}

export type CoreSelectTextFieldProps<Value> = Omit<CoreTextFieldProps, "select" | "error" | "inputRef"> & {
	label: string;
	useFloatingLabel?: boolean;
	SelectProps?: Omit<SelectProps<Value>, "renderValue">;
	renderValue: (value: Value) => ReactNode;
	error?: ReactNode;
	shadowDomContainer?: HTMLElement | null /**Keyboard breaks in shadowdom */;
};

export function CoreSelectTextField<Value>({
	label,
	useFloatingLabel,
	SelectProps,
	renderValue,
	shadowDomContainer,
	...textFieldProps
}: CoreSelectTextFieldProps<Value>) {
	const selectRef = useRef<HTMLElement>(null);

	const theme = useMemo(() => createTheme(themeOptions, selectThemeOptions), []);
	return (
		<ThemeProvider theme={theme}>
			<CoreTextField
				{...textFieldProps}
				inputRef={selectRef}
				select
				{...(useFloatingLabel ? { label, useFloatingLabel: true } : {})}
				SelectProps={{
					...SelectProps,
					renderValue: (value) => (value != null ? renderValue(value as Value) : !useFloatingLabel ? label : undefined),

					MenuProps: {
						transformOrigin: {
							vertical: -5,
							horizontal: "center",
						},
						...SelectProps?.MenuProps,
						container: shadowDomContainer,
						...(shadowDomContainer && {
							onKeyDown: (e) => handleShadowDomArrowKeys(e, shadowDomContainer),
						}),
					},
				}}
			/>
		</ThemeProvider>
	);
}
